import React from 'react';
import { View, Text, StyleSheet, Image, Dimensions } from 'react-native';
import {
  Svg,
  Defs,
  RadialGradient,
  Stop,
  Rect,
  Circle,
} from 'react-native-svg';
import { PADRINHO_LOGO_WHITE, SOBRIETY_STAR_BLUE } from '@/constants/sobrietyCardImages';
import { useLanguage } from '@/contexts/LanguageContext';
import { format } from 'date-fns';
import { ptBR, enUS, es } from 'date-fns/locale';

interface Props {
  days: number;
}

const SCREEN_WIDTH = Dimensions.get('window').width;
const CARD_WIDTH = SCREEN_WIDTH - 48;
const CARD_HEIGHT = 390;

export default function SobrietyCardTemplate3({ days }: Props) {
  const { t, language } = useLanguage();

  const getLocale = () => {
    switch (language) {
      case 'pt': return ptBR;
      case 'es': return es;
      default: return enUS;
    }
  };

  const formatCurrentDate = () => {
    const now = new Date();
    const locale = getLocale();
    if (language === 'pt' || language === 'es') {
      return format(now, "d 'de' MMMM 'de' yyyy", { locale });
    }
    return format(now, 'MMMM d, yyyy', { locale });
  };

  return (
    <View style={styles.card} collapsable={false}>

      {/* 1. Decorative radial glow overlay — same as slot 2, absolute, bottom of stack */}
      <View style={styles.decorativeLayer}>
        <Svg
          width={CARD_WIDTH}
          height={CARD_HEIGHT}
          viewBox={`0 0 ${CARD_WIDTH} ${CARD_HEIGHT}`}
        >
          <Defs>
            <RadialGradient id="t3glow1" cx="85%" cy="8%" rx="55%" ry="35%">
              <Stop offset="0%" stopColor="#FFFFFF" stopOpacity={0.18} />
              <Stop offset="100%" stopColor="#FFFFFF" stopOpacity={0} />
            </RadialGradient>
            <RadialGradient id="t3glow2" cx="55%" cy="15%" rx="45%" ry="30%">
              <Stop offset="0%" stopColor="#003A5C" stopOpacity={0.5} />
              <Stop offset="100%" stopColor="#003A5C" stopOpacity={0} />
            </RadialGradient>
            <RadialGradient id="t3glow3" cx="50%" cy="90%" rx="70%" ry="55%">
              <Stop offset="0%" stopColor="#004B6B" stopOpacity={0.45} />
              <Stop offset="100%" stopColor="#004B6B" stopOpacity={0} />
            </RadialGradient>
          </Defs>
          <Rect x={0} y={0} width={CARD_WIDTH} height={CARD_HEIGHT} fill="url(#t3glow1)" />
          <Rect x={0} y={0} width={CARD_WIDTH} height={CARD_HEIGHT} fill="url(#t3glow2)" />
          <Rect x={0} y={0} width={CARD_WIDTH} height={CARD_HEIGHT} fill="url(#t3glow3)" />
        </Svg>
      </View>

      {/* 2. White wave at bottom — same circle geometry as slot 1's blue wave, but white */}
      <View style={styles.whiteWaveContainer}>
        <Svg width="1040" height="1040" viewBox="0 0 1040 1040" fill="none">
          <Circle cx="520" cy="520" r="520" fill="white" />
        </Svg>
      </View>

      {/* 3. Logo inside the white wave area — tintColor darkens the white logo image */}
      <View style={styles.logoContainer}>
        <Image
          source={{ uri: PADRINHO_LOGO_WHITE }}
          style={styles.logoImage}
          resizeMode="contain"
          tintColor="#151515"
        />
      </View>

      {/* 4. Badge — normal flow, renders above background layers */}
      <View style={styles.badgeContainer}>
        <Image
          source={{ uri: SOBRIETY_STAR_BLUE }}
          style={styles.badgeImage}
          resizeMode="contain"
        />
      </View>

      {/* 5. Text — normal flow */}
      <View style={styles.textContainer}>
        <Text style={styles.mainText}>
          {t('sobriety.shareCard.line1')}{'\n'}
          <Text style={styles.daysText}>{days} {t('sobriety.shareCard.days')}</Text>
          {' '}{t('sobriety.shareCard.line2')}
        </Text>
        <Text style={styles.dateText}>{formatCurrentDate()}</Text>
      </View>

    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    width: CARD_WIDTH,
    height: CARD_HEIGHT,
    backgroundColor: '#669AB7',
    borderRadius: 14,
    borderWidth: 1,
    borderColor: '#F0EEE4',
    overflow: 'hidden',
    alignItems: 'center',
    position: 'relative',
  },
  decorativeLayer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  whiteWaveContainer: {
    position: 'absolute',
    bottom: -955,
    left: -353,
    width: 1040,
    height: 1040,
  },
  logoContainer: {
    position: 'absolute',
    bottom: 20,
    left: 0,
    right: 0,
    alignItems: 'center',
    justifyContent: 'center',
  },
  logoImage: {
    width: 120,
    height: 24,
  },
  badgeContainer: {
    marginTop: 42,
    alignItems: 'center',
    justifyContent: 'center',
  },
  badgeImage: {
    width: 133,
    height: 133,
  },
  textContainer: {
    alignItems: 'center',
    gap: 4,
    marginTop: 16,
    paddingHorizontal: 20,
  },
  mainText: {
    fontFamily: 'Sentient-Regular',
    fontSize: 20,
    fontStyle: 'italic',
    fontWeight: '400',
    lineHeight: 24,
    color: '#FFFFFF',
    textAlign: 'center',
  },
  daysText: {
    fontFamily: 'Sentient-Regular',
    fontSize: 20,
    fontStyle: 'italic',
    fontWeight: '400',
    color: '#FFFFFF',
  },
  dateText: {
    fontFamily: 'Inter_500Medium',
    fontSize: 12,
    fontWeight: '500',
    lineHeight: 17,
    color: '#F0EEE4',
    textAlign: 'center',
    marginTop: 4,
  },
});
